import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import {
  getNotificationConfiguration,
  getNotificationPreferences,
  NOTIFICATION_DEVICE_STATUS,
  previewNotifications,
  saveNotificationPreferences,
  sendTestNotification,
  subscribeCurrentDevice,
  unsubscribeCurrentDevice
} from '../services/notificationService';
import useNotificationDeviceStatus from '../hooks/useNotificationDeviceStatus';
import styles from './NotificationSettingsPage.module.css';

const TEXT = {
  ko: {
    announcement: '새 공지 알림', title: '알림 설정', back: '뒤로', loading: '알림 설정을 불러오는 중…', unavailable: '알림을 사용할 수 없습니다.',
    serverDisabled: '서버 알림 기능이 아직 활성화되지 않았습니다.', ineligible: '현재 계정은 일정 알림 대상이 아닙니다.',
    unsupported: '이 브라우저는 Web Push를 지원하지 않습니다.', install: 'iPhone에서는 이 웹앱을 홈 화면에 추가한 뒤 실행해야 알림을 받을 수 있습니다.',
    denied: '알림 권한이 차단되어 있습니다. iPhone 설정에서 이 웹앱의 알림을 허용해주세요.',
    permissionDefault: '알림을 켜면 브라우저가 권한을 요청합니다.', permissionGranted: '이 기기의 알림 권한이 허용되었습니다.',
    master: '일정 알림 사용', masterHelp: '확정 여부와 관계없이 선택한 본인 타투 일정만 알립니다.',
    statuses: '알림 대상 상태', pending: '대기 중', date_confirmed: '날짜 확정', deposit_confirmed: '선금 확정', confirmed: '확정',
    timezone: '알림 시간대', details: '잠금 화면에 일정 상세 표시', summary: '하루 전 일정 요약', reminder: '당일 첫 일정 알림', eachReminder: '각 일정 시작 알림',
    enabled: '사용', lead: '사전 알림', days: '일', hours: '시간', minutes: '분', relative: '첫 일정 기준', fixed: '전날 고정 시각',
    day: '일', hour: '시간', minute: '분',
    fixedTime: '고정 시각', firstOnly: '첫 일정만', allSchedules: '모든 일정',
    preview: '미리보기', test: '테스트 알림', reset: '기본값으로 초기화', unsubscribe: '현재 기기 구독 해제',
    saving: '저장 중…', autoSaved: '자동 저장됨', autoSaveError: '자동 저장 실패', subscribed: '현재 기기의 알림이 연결되었습니다.', unsubscribed: '현재 기기 구독을 해제했습니다.',
    testSent: '테스트 알림을 요청했습니다.', conflict: '다른 기기에서 설정이 바뀌었습니다. 최신 설정을 불러왔습니다.',
    error: '요청을 처리하지 못했습니다.', currentDeviceOn: '현재 기기: 연결됨', currentDeviceOff: '현재 기기: 연결되지 않음', currentDeviceChecking: '현재 기기: 연결 확인 중…'
  },
  en: {
    announcement: 'New announcements', title: 'Notification settings', back: 'Back', loading: 'Loading notification settings…', unavailable: 'Notifications are unavailable.',
    serverDisabled: 'Server notifications are not enabled yet.', ineligible: 'This account is not eligible for schedule notifications.',
    unsupported: 'This browser does not support Web Push.', install: 'On iPhone, add this web app to the Home Screen and open it there to receive notifications.',
    denied: 'Notifications are blocked. Allow notifications for this web app in iPhone Settings.',
    permissionDefault: 'Your browser will ask for permission when you enable notifications.', permissionGranted: 'Notifications are allowed on this device.',
    master: 'Schedule notifications', masterHelp: 'Only your selected active tattoo schedules are included, whether confirmed or not.',
    statuses: 'Included statuses', pending: 'Pending', date_confirmed: 'Date confirmed', deposit_confirmed: 'Deposit confirmed', confirmed: 'Confirmed',
    timezone: 'Notification time zone', details: 'Show schedule details on the lock screen', summary: 'Day-before summary', reminder: 'First schedule reminder', eachReminder: 'Reminder before every schedule',
    enabled: 'Enabled', lead: 'Lead time', days: 'days', hours: 'hours', minutes: 'minutes', relative: 'Relative to first schedule', fixed: 'Fixed time on previous day',
    day: 'day', hour: 'hour', minute: 'minute',
    fixedTime: 'Fixed time', firstOnly: 'First schedule only', allSchedules: 'All schedules',
    preview: 'Preview', test: 'Test notification', reset: 'Reset defaults', unsubscribe: 'Disconnect this device',
    saving: 'Saving…', autoSaved: 'Saved automatically', autoSaveError: 'Automatic save failed', subscribed: 'Notifications are connected on this device.', unsubscribed: 'This device was disconnected.',
    testSent: 'Test notification requested.', conflict: 'Settings changed on another device. The latest settings were loaded.',
    error: 'The request could not be completed.', currentDeviceOn: 'Current device: connected', currentDeviceOff: 'Current device: not connected', currentDeviceChecking: 'Current device: checking connection…'
  },
  fr: {
    announcement: 'Nouvelles annonces', title: 'Paramètres de notification', back: 'Retour', loading: 'Chargement des paramètres de notification…', unavailable: 'Les notifications ne sont pas disponibles.',
    serverDisabled: 'Les notifications serveur ne sont pas encore activées.', ineligible: "Ce compte n'est pas éligible aux notifications de planning.",
    unsupported: 'Ce navigateur ne prend pas en charge Web Push.', install: "Sur iPhone, ajoutez cette app web à l’écran d’accueil puis ouvrez-la depuis celui-ci.",
    denied: "Les notifications sont bloquées. Autorisez-les pour cette app web dans les réglages de l’iPhone.",
    permissionDefault: "Le navigateur demandera l’autorisation lors de l’activation.", permissionGranted: 'Les notifications sont autorisées sur cet appareil.',
    master: 'Notifications de planning', masterHelp: 'Seuls vos rendez-vous tatouage actifs sélectionnés sont inclus, confirmés ou non.',
    statuses: 'Statuts inclus', pending: 'En attente', date_confirmed: 'Date confirmée', deposit_confirmed: 'Acompte confirmé', confirmed: 'Confirmé',
    timezone: 'Fuseau horaire', details: "Afficher les détails sur l’écran verrouillé", summary: 'Résumé de la veille', reminder: 'Rappel du premier rendez-vous', eachReminder: 'Rappel avant chaque rendez-vous',
    enabled: 'Activé', lead: 'Délai', days: 'jours', hours: 'heures', minutes: 'minutes', relative: 'Selon le premier rendez-vous', fixed: 'Heure fixe la veille',
    day: 'jour', hour: 'heure', minute: 'minute',
    fixedTime: 'Heure fixe', firstOnly: 'Premier rendez-vous', allSchedules: 'Tous les rendez-vous',
    preview: 'Aperçu', test: 'Notification test', reset: 'Valeurs par défaut', unsubscribe: 'Déconnecter cet appareil',
    saving: 'Enregistrement…', autoSaved: 'Enregistré automatiquement', autoSaveError: 'Échec de l’enregistrement automatique', subscribed: 'Notifications connectées sur cet appareil.', unsubscribed: 'Cet appareil a été déconnecté.',
    testSent: 'Notification test demandée.', conflict: 'Les paramètres ont changé sur un autre appareil. La dernière version a été chargée.',
    error: "La demande n’a pas pu être traitée.", currentDeviceOn: 'Appareil actuel : connecté', currentDeviceOff: 'Appareil actuel : non connecté', currentDeviceChecking: 'Appareil actuel : vérification de la connexion…'
  }
};

const deepClone = (value) => JSON.parse(JSON.stringify(value));

const resetPreferencesToDefaults = (configuration, preferences) => {
  const defaultRules = configuration?.defaults?.rules || {};
  const currentRules = preferences?.rules || {};
  const ruleTypes = new Set([...Object.keys(defaultRules), ...Object.keys(currentRules)]);

  return {
    ...deepClone(configuration.defaults.preferences),
    revision: preferences.revision,
    rules: Object.fromEntries([...ruleTypes].map((ruleType) => [
      ruleType,
      deepClone(defaultRules[ruleType] || currentRules[ruleType])
    ]))
  };
};

export default function NotificationSettingsPage() {
  const { userInfo } = useContext(UserContext);
  const navigate = useNavigate();
  const lang = ['ko', 'en', 'fr'].includes(userInfo?.preferred_language) ? userInfo.preferred_language : 'ko';
  const text = TEXT[lang];
  const userId = userInfo?.user_id;
  const { status: deviceStatus } = useNotificationDeviceStatus(userId, userInfo?.level);
  const runtime = deviceStatus.runtime;
  const deviceConnected = deviceStatus.state === NOTIFICATION_DEVICE_STATUS.ACTIVE;
  const [configuration, setConfiguration] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [previews, setPreviews] = useState([]);
  const [message, setMessage] = useState(null);
  const [busyAction, setBusyAction] = useState(null);
  const [saveState, setSaveState] = useState('idle');
  const preferencesRef = useRef(null);
  const revisionRef = useRef(null);
  const editVersionRef = useRef(0);
  const autosaveTimerRef = useRef(null);
  const saveQueueRef = useRef(Promise.resolve());

  const replacePreferences = useCallback((nextPreferences) => {
    preferencesRef.current = nextPreferences;
    revisionRef.current = nextPreferences?.revision ?? revisionRef.current;
    setPreferences(nextPreferences);
  }, []);

  const persistLatestPreferences = useCallback(() => {
    if (!preferencesRef.current) return;
    setSaveState('saving');

    saveQueueRef.current = saveQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        const saveVersion = editVersionRef.current;
        const candidate = {
          ...deepClone(preferencesRef.current),
          revision: revisionRef.current
        };

        try {
          const saved = await saveNotificationPreferences(candidate, userId);
          revisionRef.current = saved.revision;
          const hasNewerEdits = editVersionRef.current !== saveVersion;
          const nextPreferences = hasNewerEdits
            ? { ...preferencesRef.current, revision: saved.revision }
            : saved;
          preferencesRef.current = nextPreferences;
          setPreferences(nextPreferences);
          if (!hasNewerEdits) setSaveState('saved');
        } catch (error) {
          if (error.status === 409 && error.data?.preferences) {
            replacePreferences(error.data.preferences);
            setMessage({ type: 'error', text: text.conflict });
          } else {
            setMessage({ type: 'error', text: error.message || text.error });
          }
          setSaveState('error');
        }
      });
  }, [replacePreferences, text.conflict, text.error, userId]);

  const scheduleAutosave = useCallback((nextPreferences, { immediate = false } = {}) => {
    preferencesRef.current = nextPreferences;
    editVersionRef.current += 1;
    setPreferences(nextPreferences);
    setMessage(null);
    setSaveState('saving');
    clearTimeout(autosaveTimerRef.current);

    if (immediate) {
      persistLatestPreferences();
      return;
    }
    autosaveTimerRef.current = setTimeout(persistLatestPreferences, 500);
  }, [persistLatestPreferences]);

  useEffect(() => () => clearTimeout(autosaveTimerRef.current), []);

  const load = useCallback(async () => {
    setBusyAction('load');
    setMessage(null);
    try {
      const nextConfiguration = await getNotificationConfiguration();
      setConfiguration(nextConfiguration);
      if (nextConfiguration.eligible_user && nextConfiguration.enabled) {
        const nextPreferences = await getNotificationPreferences(userId);
        replacePreferences(nextPreferences);
      } else if (nextConfiguration.eligible_user) {
        replacePreferences({
          ...deepClone(nextConfiguration.defaults.preferences),
          rules: deepClone(nextConfiguration.defaults.rules)
        });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message || text.error });
    } finally {
      setBusyAction(null);
    }
  }, [replacePreferences, text.error, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const updatePreference = (field, value, options) => {
    scheduleAutosave({ ...preferencesRef.current, [field]: value }, options);
  };

  const updateRule = (ruleType, field, value, options) => {
    const current = preferencesRef.current;
    scheduleAutosave({
      ...current,
      rules: {
        ...current.rules,
        [ruleType]: {
          ...current.rules[ruleType],
          [field]: value
        }
      }
    }, options);
  };

  const toggleStatus = (status) => {
    const selected = new Set(preferences.eligible_event_statuses);
    if (selected.has(status)) selected.delete(status);
    else selected.add(status);
    updatePreference('eligible_event_statuses', [...selected], { immediate: true });
  };

  const handleMasterChange = async (enabled) => {
    setMessage(null);
    if (enabled && !deviceConnected) {
      setBusyAction('subscribe');
      try {
        await subscribeCurrentDevice(userId);
      } catch (error) {
        setMessage({ type: 'error', text: error.message || text.error });
        setBusyAction(null);
        return;
      }
      setBusyAction(null);
    }
    updatePreference('enabled', enabled, { immediate: true });
  };

  const handleSubscribe = async () => {
    setBusyAction('subscribe');
    setMessage(null);
    try {
      await subscribeCurrentDevice(userId);
      setMessage({ type: 'success', text: text.subscribed });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || text.error });
    } finally {
      setBusyAction(null);
    }
  };

  const handleUnsubscribe = async () => {
    setBusyAction('unsubscribe');
    setMessage(null);
    try {
      await unsubscribeCurrentDevice(userId);
      setMessage({ type: 'success', text: text.unsubscribed });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || text.error });
    } finally {
      setBusyAction(null);
    }
  };

  const handlePreview = async () => {
    setBusyAction('preview');
    try {
      setPreviews(await previewNotifications(preferences, lang));
    } catch (error) {
      setMessage({ type: 'error', text: error.message || text.error });
    } finally {
      setBusyAction(null);
    }
  };

  const handleTest = async () => {
    setBusyAction('test');
    setMessage(null);
    try {
      if (!deviceConnected) {
        await subscribeCurrentDevice(userId);
      }
      await sendTestNotification(userId);
      setMessage({ type: 'success', text: text.testSent });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || text.error });
    } finally {
      setBusyAction(null);
    }
  };

  const blocker = useMemo(() => {
    if (!configuration) return null;
    if (!configuration.eligible_user) return text.ineligible;
    if (!configuration.enabled) return text.serverDisabled;
    if (!runtime.supported) return text.unsupported;
    if (runtime.isIos && !runtime.isStandalone) return text.install;
    return null;
  }, [configuration, runtime, text]);

  if (busyAction === 'load' && !configuration) {
    return <main className={styles.page}><p className={styles.loading}>{text.loading}</p></main>;
  }

  const statusOptions = configuration?.options?.eligible_event_statuses || [];
  const summaryRule = preferences?.rules?.day_before_summary;
  const reminderRule = preferences?.rules?.schedule_reminder;
  const eachScheduleRule = preferences?.rules?.each_schedule_reminder;
  const leadValueOptions = configuration?.options?.lead_value_options_by_unit;
  const timeZoneOptions = [...new Set([
    ...(configuration?.options?.time_zones || []),
    preferences?.timezone
  ].filter(Boolean))];
  const isBusy = Boolean(busyAction);
  const currentDeviceText = deviceStatus.state === NOTIFICATION_DEVICE_STATUS.ACTIVE
    ? text.currentDeviceOn
    : deviceStatus.state === NOTIFICATION_DEVICE_STATUS.CHECKING
      ? text.currentDeviceChecking
      : text.currentDeviceOff;
  const canDisconnectDevice = deviceConnected || deviceStatus.browserSubscribed;

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <button type="button" className={styles.backButton} onClick={() => navigate(-1)} aria-label={text.back}>←</button>
        <h1>{text.title}</h1>
      </header>

      {blocker && <div className={styles.notice} role="status">{blocker}</div>}
      {!blocker && runtime.permission === 'denied' && <div className={styles.notice}>{text.denied}</div>}
      {!blocker && runtime.permission === 'default' && <div className={styles.notice}>{text.permissionDefault}</div>}
      {!blocker && runtime.permission === 'granted' && <div className={styles.notice}>{text.permissionGranted}</div>}
      {message && <div className={`${styles.message} ${styles[message.type]}`} role="status">{message.text}</div>}

      {preferences && (
        <>
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <div><h2>{text.master}</h2><p>{text.masterHelp}</p></div>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={preferences.enabled}
                  onChange={(event) => handleMasterChange(event.target.checked)}
                  disabled={Boolean(blocker) || busyAction === 'subscribe'}
                  aria-label={text.master}
                />
                <span aria-hidden="true" />
              </label>
            </div>
            <div className={styles.deviceRow}>
              <span>{currentDeviceText}</span>
              {!deviceConnected && <button type="button" onClick={handleSubscribe} disabled={Boolean(blocker) || isBusy}>{text.enabled}</button>}
            </div>
            <label className={styles.field}>
              <span>{text.timezone}</span>
              <select
                value={preferences.timezone}
                onChange={(event) => updatePreference('timezone', event.target.value, { immediate: true })}
              >
                {timeZoneOptions.map((timeZone) => (
                  <option key={timeZone} value={timeZone}>{timeZone.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </label>
            <label className={styles.checkboxRow}>
              <input type="checkbox" checked={preferences.show_schedule_details} onChange={(event) => updatePreference('show_schedule_details', event.target.checked, { immediate: true })} />
              <span>{text.details}</span>
            </label>
          </section>

          {userInfo?.level === 'staff_artist' && (
            <section className={styles.section}>
              <label className={styles.checkboxRow}>
                <input type="checkbox" checked={preferences.announcement_enabled ?? true} onChange={event => updatePreference('announcement_enabled',event.target.checked,{immediate:true})}/>
                <span>{text.announcement}</span>
              </label>
            </section>
          )}
          <section className={styles.section}>
            <h2>{text.statuses}</h2>
            <div className={styles.statusGrid}>
              {statusOptions.map((status) => (
                <label key={status} className={styles.checkboxRow}>
                  <input type="checkbox" checked={preferences.eligible_event_statuses.includes(status)} onChange={() => toggleStatus(status)} />
                  <span>{text[status]}</span>
                </label>
              ))}
            </div>
          </section>

          {summaryRule && (
            <RuleEditor
              title={text.summary}
              rule={summaryRule}
              text={text}
              onChange={(field, value, options) => updateRule('day_before_summary', field, value, options)}
              leadValueOptions={leadValueOptions}
              showDeliveryMode
            />
          )}
          {reminderRule && (
            <RuleEditor
              title={text.reminder}
              rule={reminderRule}
              text={text}
              onChange={(field, value, options) => updateRule('schedule_reminder', field, value, options)}
              leadValueOptions={leadValueOptions}
              showScope
            />
          )}
          {eachScheduleRule && (
            <RuleEditor
              title={text.eachReminder}
              rule={eachScheduleRule}
              text={text}
              onChange={(field, value, options) => updateRule('each_schedule_reminder', field, value, options)}
              leadValueOptions={leadValueOptions}
              leadUnits={['minutes']}
            />
          )}

          <section className={styles.actionsSection}>
            <button type="button" onClick={handlePreview} disabled={isBusy}>{text.preview}</button>
            <button type="button" onClick={handleTest} disabled={Boolean(blocker) || isBusy}>{text.test}</button>
            <button
              type="button"
              onClick={() => scheduleAutosave(resetPreferencesToDefaults(configuration, preferences), { immediate: true })}
              disabled={isBusy}
            >
              {text.reset}
            </button>
            <button type="button" onClick={handleUnsubscribe} disabled={!canDisconnectDevice || isBusy}>{text.unsubscribe}</button>
            {saveState !== 'idle' && (
              <p className={`${styles.saveStatus} ${saveState === 'error' ? styles.saveError : ''}`} role="status">
                {saveState === 'saving' ? text.saving : saveState === 'saved' ? text.autoSaved : text.autoSaveError}
              </p>
            )}
          </section>

          {previews.length > 0 && (
            <section className={styles.previewSection} aria-live="polite">
              {previews.map((preview) => (
                <article key={preview.rule_type}><strong>{preview.title}</strong><p>{preview.body}</p></article>
              ))}
            </section>
          )}
        </>
      )}
    </main>
  );
}

function RuleEditor({
  title,
  rule,
  text,
  onChange,
  showDeliveryMode = false,
  showScope = false,
  leadUnits = ['minutes', 'hours', 'days'],
  leadValueOptions = {}
}) {
  const configuredValues = leadValueOptions[rule.lead_unit] || [];
  const currentLeadValue = Number(rule.lead_value);
  const selectableValues = [...new Set([...configuredValues, currentLeadValue])]
    .filter(Number.isInteger)
    .sort((left, right) => left - right);
  const showLeadUnitPicker = leadUnits.length > 1;

  const formatLeadOption = (value) => {
    if (showLeadUnitPicker) return String(value);
    const singularKey = rule.lead_unit.replace(/s$/, '');
    const unitLabel = value === 1 ? text[singularKey] : text[rule.lead_unit];
    return `${value} ${unitLabel}`;
  };

  const updateLeadUnit = (value) => {
    const nextUnitValues = leadValueOptions[value] || [];
    const nextLeadValue = nextUnitValues.includes(currentLeadValue)
      ? currentLeadValue
      : (nextUnitValues[0] ?? currentLeadValue);
    onChange('lead_unit', value);
    onChange('lead_mode', value === 'days' ? 'calendar' : 'duration');
    if (nextLeadValue !== currentLeadValue) {
      onChange('lead_value', nextLeadValue);
    }
  };

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2>{title}</h2>
        <label className={styles.switch}>
          <input
            type="checkbox"
            checked={rule.enabled}
            onChange={(event) => onChange('enabled', event.target.checked)}
            aria-label={title}
          />
          <span aria-hidden="true" />
        </label>
      </div>
      <div className={styles.inlineFields}>
        <label className={styles.field}>
          <span>{text.lead}</span>
          <select
            value={rule.lead_value}
            onChange={(event) => onChange('lead_value', Number(event.target.value), { immediate: true })}
          >
            {selectableValues.map((value) => (
              <option key={value} value={value}>{formatLeadOption(value)}</option>
            ))}
          </select>
        </label>
        {showLeadUnitPicker && (
          <label className={styles.field}>
            <span>&nbsp;</span>
            <select value={rule.lead_unit} onChange={(event) => updateLeadUnit(event.target.value)}>
              {leadUnits.map((unit) => <option key={unit} value={unit}>{text[unit]}</option>)}
            </select>
          </label>
        )}
      </div>
      {showDeliveryMode && (
        <>
          <label className={styles.field}><span>{text.lead}</span><select value={rule.delivery_mode} onChange={(event) => {
            const value = event.target.value;
            onChange('delivery_mode', value);
            onChange('delivery_local_time', value === 'fixed_local_time' ? (rule.delivery_local_time || '10:00') : null);
            if (value === 'fixed_local_time') {
              updateLeadUnit('days');
            }
          }}><option value="relative_to_first_schedule">{text.relative}</option><option value="fixed_local_time">{text.fixed}</option></select></label>
          {rule.delivery_mode === 'fixed_local_time' && <label className={styles.field}><span>{text.fixedTime}</span><input type="time" value={rule.delivery_local_time || '10:00'} onChange={(event) => onChange('delivery_local_time', event.target.value)} /></label>}
        </>
      )}
      {showScope && <label className={styles.field}><span>{text.reminder}</span><select value={rule.schedule_scope} onChange={(event) => onChange('schedule_scope', event.target.value)}><option value="first_schedule">{text.firstOnly}</option><option value="all_schedules">{text.allSchedules}</option></select></label>}
    </section>
  );
}
